import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  ChevronLeft,
  Crown,
  FileSignature,
  Loader2,
  Mail,
  Receipt,
  Trash2,
} from "lucide-react";
import { useOrg } from "@/lib/org/OrgContext";
import { ApiError } from "@/api/fetch";
import { useToastMutation } from "@/lib/mutations";
import {
  employeesApi,
  employeesQueries,
  type EmploymentDetailDto,
  type EmploymentWageRuleDto,
  type TenureBlockDto,
} from "@/lib/employees/api";
import { companiesQueries, isManager } from "@/lib/companies/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { PageContainer } from "@/components/layout/WorkspaceShell";
import { formatNumber, formatRelative } from "@/lib/format";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/o/$slug/employees/$companyId/$ordioIdentityId")({
  component: EmployeeDetailPage,
});

function EmployeeDetailPage() {
  const org = useOrg();
  const { companyId, ordioIdentityId } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const employments = useQuery(
    employeesQueries.detailByOrdioIdentity(org.slug, companyId, ordioIdentityId),
  );
  const companies = useQuery(companiesQueries.all());

  const company = companies.data?.find((c) => c.id === companyId);
  const canManage = company ? isManager(company.role) : false;

  const release = useToastMutation({
    mutationFn: () => employeesApi.releaseOrphanWages(org.slug, companyId, ordioIdentityId),
    toasts: {
      success: (data) => {
        const n = (data as { deleted: number } | undefined)?.deleted ?? 0;
        return `${n} Ordio-Lohnregel${n === 1 ? "" : "n"} gelöscht.`;
      },
    },
    onSuccess: () => {
      // Both the directory + this detail need to refresh — wage rules are gone,
      // employment status flips.
      queryClient.invalidateQueries({
        queryKey: employeesQueries.directory(org.slug, companyId).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: employeesQueries.detailByOrdioIdentity(org.slug, companyId, ordioIdentityId).queryKey,
      });
    },
  });

  return (
    <PageContainer className="max-w-6xl">
      <div className="mb-4">
        <Link
          to="/o/$slug/employees"
          params={{ slug: org.slug }}
          search={{ companyId }}
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="h-4 w-4" />
          Personal
        </Link>
      </div>

      {employments.isLoading && <DetailSkeleton />}
      {employments.error && (
        <ErrorBox error={employments.error as Error} slug={org.slug} companyId={companyId} />
      )}

      {employments.data && employments.data.length === 0 && (
        <NotInJaveedState
          slug={org.slug}
          companyId={companyId}
          ordioIdentityId={ordioIdentityId}
        />
      )}

      {employments.data?.map((detail) => (
        <EmploymentSection
          key={detail.employment.id}
          detail={detail}
          currency={company?.currency ?? "EUR"}
          canManage={canManage}
          onAmend={() => navigate({ to: "/o/$slug/employees", params: { slug: org.slug } })}
          onRelease={() => {
            if (
              window.confirm(
                "Alle aktiven Ordio-Lohnregeln dieser Person löschen? Die Person kann anschließend neu durch den Vertragsassistenten geführt werden.",
              )
            ) {
              release.mutate(undefined);
            }
          }}
          releasing={release.isPending}
        />
      ))}
    </PageContainer>
  );
}

// ── One employment block ────────────────────────────────────────────────────

function EmploymentSection({
  detail,
  currency,
  canManage,
  onAmend,
  onRelease,
  releasing,
}: {
  detail: EmploymentDetailDto;
  currency: string;
  canManage: boolean;
  onAmend: () => void;
  onRelease: () => void;
  releasing: boolean;
}) {
  const e = detail.employment;
  const status = e.hasOrphanWageRules
    ? ("orphan-wages" as const)
    : e.hasJaveedTenure
      ? ("fully-synced" as const)
      : ("not-in-javeed" as const);
  const isAmendable = e.hasJaveedTenure && !e.hasOrphanWageRules;

  return (
    <section className="mb-8">
      <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-semibold tracking-tight">{e.employeeDisplayName}</h1>
            <StatusPill status={status} />
            {e.status === "Ended" && (
              <span className="rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                Beendet
              </span>
            )}
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            Ordio-ID {e.ordioEmployeeId} · seit {formatRelative(e.startedOn) ?? e.startedOn}
            {e.endedOn && ` · beendet ${formatRelative(e.endedOn) ?? e.endedOn}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            disabled={!isAmendable || !canManage}
            onClick={onAmend}
            title={
              !canManage
                ? "Nur Manager können Vertragsergänzungen anlegen."
                : !isAmendable
                  ? "Eligibilität: nur möglich, wenn alle Lohnregeln eine TenureBlock-Verankerung haben."
                  : "Vertragsergänzung anlegen"
            }
          >
            <FileSignature className="h-4 w-4" />
            Vertrag anpassen
          </Button>
          {e.hasOrphanWageRules && canManage && (
            <Button
              type="button"
              variant="outline"
              onClick={onRelease}
              disabled={releasing}
              title="Aktive Ordio-Lohnregeln löschen, damit die Person neu durch den Vertragsassistenten kann"
            >
              {releasing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              Lohnregeln löschen
            </Button>
          )}
        </div>
      </header>

      <div className="grid gap-4 lg:grid-cols-[1fr_280px]">
        <div className="space-y-4">
          <CurrentWageCard employment={detail.employment} currency={currency} />
          <TenureTimeline blocks={detail.tenureBlocks} />
          <WageRulesTable rules={detail.wageRules} currency={currency} />
        </div>

        <SidePanel detail={detail} />
      </div>
    </section>
  );
}

// ── Cards ───────────────────────────────────────────────────────────────────

function CurrentWageCard({
  employment,
  currency,
}: {
  employment: EmploymentDetailDto["employment"];
  currency: string;
}) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Aktueller Lohn</CardTitle>
          <Receipt className="h-4 w-4 text-muted-foreground" />
        </div>
        <CardDescription>Wert und Typ der aktiven Ordio-Lohnregel.</CardDescription>
      </CardHeader>
      <CardContent>
        {employment.currentWageAmount != null ? (
          <div className="flex items-baseline gap-3">
            <div className="text-3xl font-semibold tabular-nums">
              {formatNumber(employment.currentWageAmount)}
            </div>
            <div className="text-sm text-muted-foreground">{currency}</div>
            {employment.currentWageType && (
              <div className="text-xs uppercase text-muted-foreground">
                {employment.currentWageType}
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">Keine aktive Lohnregel in Ordio.</p>
        )}
      </CardContent>
    </Card>
  );
}

function TenureTimeline({ blocks }: { blocks: TenureBlockDto[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>TenureBlock-Verlauf</CardTitle>
        <CardDescription>
          Dokumentierte Instrumente (Verträge + Vertragsergänzungen) in chronologischer Reihenfolge.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {blocks.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Noch keine TenureBlocks – das Arbeitsverhältnis wurde nicht über Javeed dokumentiert.
          </p>
        ) : (
          <ol className="relative space-y-3 border-l pl-4">
            {[...blocks]
              .sort((a, b) => +new Date(a.effectiveFrom) - +new Date(b.effectiveFrom))
              .map((b) => (
                <li key={b.id} className="relative">
                  <span
                    className={cn(
                      "absolute -left-[21px] mt-1 inline-block h-3 w-3 rounded-full border-2 border-background",
                      kindBgColor(b.kind),
                    )}
                  />
                  <div className="text-sm font-medium">{kindLabel(b.kind)}</div>
                  <div className="text-xs text-muted-foreground">
                    Wirksam ab {b.effectiveFrom}
                    {b.effectiveTo && ` bis ${b.effectiveTo}`}
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    erstellt {formatRelative(b.createdAt) ?? "—"}
                  </div>
                </li>
              ))}
          </ol>
        )}
      </CardContent>
    </Card>
  );
}

function WageRulesTable({
  rules,
  currency,
}: {
  rules: EmploymentWageRuleDto[];
  currency: string;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Lohnregeln</CardTitle>
        <CardDescription>
          Spiegel der Ordio-Wage-History. Regeln ohne TenureBlock sind „orphan" und sollten via
          Adoption oder Release behandelt werden.
        </CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        {rules.length === 0 ? (
          <p className="p-6 text-sm text-muted-foreground">Keine Lohnregeln.</p>
        ) : (
          <div className="overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Wirksam</th>
                  <th className="px-3 py-2 text-left font-medium">Typ</th>
                  <th className="px-3 py-2 text-right font-medium">Betrag</th>
                  <th className="px-3 py-2 text-right font-medium">Anchor</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {[...rules]
                  .sort((a, b) => +new Date(b.effectiveFrom) - +new Date(a.effectiveFrom))
                  .map((r) => (
                    <tr key={r.id}>
                      <td className="px-3 py-2 text-xs text-muted-foreground">
                        {r.effectiveFrom}
                        {r.effectiveTo ? ` – ${r.effectiveTo}` : " – heute"}
                      </td>
                      <td className="px-3 py-2 text-xs uppercase">{r.wageType}</td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {formatNumber(r.amount)} {currency}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {r.tenureBlockId ? (
                          <span className="inline-flex items-center gap-1 text-emerald-600">
                            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                            verankert
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-amber-700">
                            <AlertTriangle className="h-3.5 w-3.5" />
                            orphan
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function SidePanel({ detail }: { detail: EmploymentDetailDto }) {
  const e = detail.employment;
  return (
    <aside className="space-y-4 lg:sticky lg:top-4 lg:self-start">
      <div className="rounded-lg border bg-card p-4 text-sm">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
          <Crown className="h-3.5 w-3.5" />
          Arbeitsverhältnis
        </div>
        <div className="mt-2 text-xs">
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground">ID</span>
            <span className="truncate font-mono text-[10px]">{e.id}</span>
          </div>
          <div className="mt-1 flex justify-between gap-2">
            <span className="text-muted-foreground">Status</span>
            <span>{e.status}</span>
          </div>
          <div className="mt-1 flex justify-between gap-2">
            <span className="text-muted-foreground">Erstellt</span>
            <span>{formatRelative(e.createdAt) ?? "—"}</span>
          </div>
          {e.lastReconciledAt && (
            <div className="mt-1 flex justify-between gap-2">
              <span className="text-muted-foreground">Letzter Sync</span>
              <span>{formatRelative(e.lastReconciledAt) ?? "—"}</span>
            </div>
          )}
        </div>
        <Separator className="my-3" />
        <div className="text-xs text-muted-foreground">
          {detail.tenureBlocks.length} TB · {detail.wageRules.length} Lohnregel
          {detail.wageRules.length === 1 ? "" : "n"}
        </div>
      </div>
    </aside>
  );
}

// ── Bits + pieces ───────────────────────────────────────────────────────────

function StatusPill({ status }: { status: "fully-synced" | "not-in-javeed" | "orphan-wages" }) {
  const map = {
    "fully-synced": { label: "In Javeed", cls: "bg-emerald-100 text-emerald-700" },
    "not-in-javeed": { label: "Nicht in Javeed", cls: "bg-muted text-muted-foreground" },
    "orphan-wages": { label: "Orphan-Wages", cls: "bg-amber-100 text-amber-800" },
  } as const;
  const { label, cls } = map[status];
  return <span className={cn("rounded-full px-2 py-0.5 text-xs", cls)}>{label}</span>;
}

function kindLabel(kind: TenureBlockDto["kind"]): string {
  switch (kind) {
    case "NewContract":
      return "Arbeitsvertrag";
    case "Amendment":
      return "Vertragsergänzung";
    case "Adoption":
      return "Adoption";
  }
}

function kindBgColor(kind: TenureBlockDto["kind"]): string {
  switch (kind) {
    case "NewContract":
      return "bg-blue-500";
    case "Amendment":
      return "bg-purple-500";
    case "Adoption":
      return "bg-amber-500";
  }
}

function NotInJaveedState({
  slug,
  companyId,
  ordioIdentityId,
}: {
  slug: string;
  companyId: string;
  ordioIdentityId: string;
}) {
  return (
    <div className="rounded-md border bg-card p-12 text-center">
      <Mail className="mx-auto h-8 w-8 text-muted-foreground" />
      <h2 className="mt-3 text-lg font-medium">Nicht in Javeed</h2>
      <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
        Diese Ordio-Identität ({ordioIdentityId.slice(0, 8)}…) hat noch kein Javeed-Arbeitsverhältnis.
        Lege über den Vertragsassistenten einen neuen Vertrag an.
      </p>
      <Link
        to="/o/$slug/employees"
        params={{ slug }}
        search={{ companyId }}
        className="mt-6 inline-block text-xs text-muted-foreground underline hover:text-foreground"
      >
        Zurück zur Liste
      </Link>
    </div>
  );
}

function DetailSkeleton() {
  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_280px]">
      <div className="space-y-3">
        <Skeleton className="h-12" />
        <Skeleton className="h-32" />
        <Skeleton className="h-48" />
      </div>
      <Skeleton className="h-32" />
    </div>
  );
}

function ErrorBox({
  error,
  slug,
  companyId,
}: {
  error: Error;
  slug: string;
  companyId: string;
}) {
  const status = error instanceof ApiError ? error.status : null;
  return (
    <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm">
      <p className="text-destructive">
        {status === 404
          ? "Mitarbeitende:r nicht gefunden oder kein Zugriff."
          : status === 403
            ? "Keine Berechtigung."
            : `Konnte Detail nicht laden: ${error.message}`}
      </p>
      <Link
        to="/o/$slug/employees"
        params={{ slug }}
        search={{ companyId }}
        className="mt-3 inline-block text-xs text-muted-foreground underline hover:text-foreground"
      >
        Zurück zur Liste
      </Link>
    </div>
  );
}

